import type { Result } from "neverthrow";
import { match } from "ts-pattern";

import type { InvalidError } from "@thalamos/common";

import type {
  JsonValue,
  SubmissionDocumentUnbranded,
} from "../../assessments/document.js";
import type {
  FramingRoleUnbranded,
  FramingTextUnbranded,
  MeaningSourceUnbranded,
  NotePlacementUnbranded,
  PresentationUnbranded,
  PresentedItemUnbranded,
  PresentedTextUnbranded,
  RequiredByUnbranded,
} from "../../assessments/presentation.js";
import {
  groupsOf,
  type PublishedInstrumentSchema,
} from "../../assessments/publishedSchema.js";
import {
  commentsRequired,
  notAsked,
  withBannersShown,
} from "./checkDocument.js";
import type { FormContext } from "./checks.js";
import {
  isCommentPlacement,
  type Banner,
  type CommentPlacement,
  type FormElement,
  type FormText,
  type Requirement,
  type Shown,
} from "./plainForm.js";

type ItemElement = Extract<FormElement, { kind: "item" }>;

type GroupElement = Extract<FormElement, { kind: "group" }>;

/**
 * The words this collector labels an item's comment box with, and their
 * language, given what the label may depend on: whether the form's rule
 * required a comment for the document's answers, and the item's answer, if
 * any.
 */
type CommentLabelOf = (
  element: ItemElement,
  answered: { commentRequired: boolean; response: JsonValue | undefined },
) => { text: string; language: string };

/** A form's requirement as evidence, which names the form's own `by_collector`, as it would any collector's. */
const requiredBy: Readonly<Record<Requirement, RequiredByUnbranded>> = {
  no: "no",
  by_instrument: "by_instrument",
  by_form: "by_collector",
};

/**
 * A form's text as evidence, naming the revision its canonical meanings are
 * from, and including the text any machine translation was made from.
 */
const presentedText = (
  text: FormText,
  source: MeaningSourceUnbranded,
): PresentedTextUnbranded =>
  match<FormText, PresentedTextUnbranded>(text)
    .with({ origin: { kind: "identifier" } }, ({ text: words }) => ({
      text: words,
      origin: { kind: "identifier" },
    }))
    .with({ origin: { kind: "canonical" } }, ({ text: words, language }) => ({
      text: words,
      language,
      origin: { kind: "canonical", from: source },
    }))
    .with({ origin: { kind: "collector" } }, ({ text: words, language }) => ({
      text: words,
      language,
      origin: { kind: "collector" },
    }))
    .with(
      { origin: { kind: "machine-translated" } },
      ({ text: words, language, origin }) => ({
        text: words,
        language,
        origin: {
          kind: "machine-translated",
          from: presentedText(origin.from, source),
          by: origin.by,
        },
      }),
    )
    .exhaustive();

/** A title and any description, as evidence. */
const presentedShown = (shown: Shown, source: MeaningSourceUnbranded) => ({
  title: presentedText(shown.title, source),
  ...(shown.description === undefined
    ? {}
    : { description: presentedText(shown.description, source) }),
});

/**
 * Where a banner beside the comment box sits on the comment's own entry: its
 * label is the comment's question, and its box the comment's answer.
 */
const onTheComment: Readonly<Record<CommentPlacement, NotePlacementUnbranded>> =
  { before_comment: "before_question", after_comment: "after_answer" };

/**
 * What the form shows for one item, as evidence: the item's own entry, then
 * its comment's, where it takes one, each framed by the group's heading and
 * the banners beside it.
 */
const entriesFor = ({
  element,
  heading,
  groupRequirement,
  commentRequired,
  response,
  source,
  commentLabelOf,
}: {
  element: ItemElement;
  heading: GroupElement | undefined;
  groupRequirement: Requirement;
  /** Whether the form's rule required a comment, given the document's answers. */
  commentRequired: boolean;
  /** The item's answer in the document; undefined where it has none. */
  response: JsonValue | undefined;
  source: MeaningSourceUnbranded;
  commentLabelOf: CommentLabelOf;
}): PresentedItemUnbranded[] => {
  const { control, banners } = element.presentation;
  const { commentAt } = element;
  const sectionFraming = (
    [
      { role: "section_title", text: heading?.presentation.title },
      {
        role: "section_description",
        text: heading?.presentation.description,
      },
    ] satisfies {
      role: Exclude<FramingRoleUnbranded, "note">;
      text: FormText | undefined;
    }[]
  ).flatMap(({ role, text }): FramingTextUnbranded[] =>
    text === undefined ? [] : [{ role, ...presentedText(text, source) }],
  );
  /**
   * The banners beside one entry, as notes framing it and as the severities in
   * the collector's own part of it, built from the one list so the nth note's
   * severity is the nth of `collector.banners`.
   */
  const placedBeside = (
    kind: string,
    beside: readonly { banner: Banner; placement: NotePlacementUnbranded }[],
  ) => ({
    notes: beside.map(({ banner, placement }): FramingTextUnbranded => ({
      role: "note",
      placement,
      ...presentedText(banner.text, source),
    })),
    collector: {
      control: kind,
      ...(beside.length === 0
        ? {}
        : {
            banners: beside.map(({ banner }) => ({
              severity: banner.severity,
            })),
          }),
    },
  });
  const besideItem = placedBeside(
    control.kind,
    banners.flatMap((banner) => {
      const { placement } = banner;

      return isCommentPlacement(placement) ? [] : [{ banner, placement }];
    }),
  );
  const besideComment = placedBeside(
    "text",
    banners.flatMap((banner) => {
      const { placement } = banner;

      return isCommentPlacement(placement)
        ? [{ banner, placement: onTheComment[placement] }]
        : [];
    }),
  );

  const itemEntry: PresentedItemUnbranded = {
    at: element.at,
    required: requiredBy[element.asking.required],
    groupRequired: requiredBy[groupRequirement],
    framing: [...sectionFraming, ...besideItem.notes],
    ...presentedShown(element.presentation, source),
    ...(control.kind === "choice"
      ? {
          answers: control.options.map(({ value, ...shown }) => ({
            value,
            ...presentedShown(shown, source),
          })),
        }
      : {}),
    collector: besideItem.collector,
  };

  return commentAt === undefined
    ? [itemEntry]
    : [
        itemEntry,
        {
          at: commentAt,
          required: commentRequired ? "by_collector" : "no",
          groupRequired: requiredBy[groupRequirement],
          framing: [...sectionFraming, ...besideComment.notes],
          title: {
            ...commentLabelOf(element, { commentRequired, response }),
            origin: { kind: "collector" },
          },
          collector: besideComment.collector,
        },
      ];
};

/**
 * The evidence of what this form showed a respondent, as it stands for a
 * document: an entry for every item the form asks given the document's
 * answers, answered or not, in the form's order, each framed by its group's
 * heading and any banners the answers show, each where it sat, with every
 * answer it offered, the control it asked for and each banner's severity. An
 * item taking a comment is followed by its comment's entry, titled by the
 * comment box's label and saying whether the form required a comment.
 *
 * Handed the document as submitted, it describes the form as it stood then,
 * not at any earlier sitting. Errs only where no validator can be built from
 * the schema.
 */
export const presentationOf = ({
  schema,
  form,
  document,
  context,
  source,
  requestedLanguage,
  commentLabelOf,
}: {
  schema: PublishedInstrumentSchema;
  form: readonly FormElement[];
  document: SubmissionDocumentUnbranded;
  context: FormContext;
  /** The revision the form was built from, whose meanings its canonical texts are. */
  source: MeaningSourceUnbranded;
  requestedLanguage: string;
  commentLabelOf: CommentLabelOf;
}): Result<PresentationUnbranded, InvalidError> => {
  const headingOf = (element: ItemElement) =>
    form.find(
      (candidate): candidate is GroupElement =>
        candidate.kind === "group" && candidate.at.group === element.at.group,
    );

  return withBannersShown({ schema, form, document, context }).andThen(
    (shown) =>
      notAsked({ schema, form: shown, document, context }).andThen((unasked) =>
        commentsRequired({ schema, form: shown, document, context }).map(
          (due) => ({
            requestedLanguage,
            items: shown.flatMap((element) => {
              if (
                element.kind !== "item" ||
                unasked.items.has(element.at.item)
              ) {
                return [];
              }

              const heading = headingOf(element);

              return entriesFor({
                element,
                heading,
                // Without a heading the form says nothing of its own about the
                // group, which leaves the instrument's requirement.
                groupRequirement:
                  heading?.asking.required ??
                  (groupsOf(schema).some(
                    ({ group, required }) =>
                      required && group === element.at.group,
                  )
                    ? "by_instrument"
                    : "no"),
                commentRequired: due.has(element.at.item),
                response:
                  document[element.at.group]?.[element.at.item]?.response,
                source,
                commentLabelOf,
              });
            }),
          }),
        ),
      ),
  );
};
